import React, { useEffect, useState, useMemo } from 'react';
import { Wrapper } from './style';
import { Link } from 'react-router-dom';
import { DatePicker, message, Select, Space } from 'antd';
import { GetMarketplaceDropdown, GetMarketplaceList, rangePresets } from '../../../config';
import Tiles from './tiles';
import ChartComponent from './chart';
import PL from './pl';
import Trends from './trends';
import { MakeApiCall } from '../../../apis';
import dayjs from 'dayjs';

const { Option } = Select;
const { RangePicker } = DatePicker;
const DEFAULT_PRODUCT_IMAGE = 'https://app.sellerview.ai/sellerview_api/images/no-product-image.png';

const Dashboard = ({ userData }) => {
  // State
  const [activeTab, setActiveTab] = useState('charts');
  const [loading, setLoading] = useState(false);
  const [marketplaceList, setMarketplaceList] = useState([]);
  const [selectedMarketplace, setSelectedMarketplace] = useState(null);
  const [selectedCurrencySign] = useState('₹');
  const [filterData, setFilterData] = useState({});
  const [selectedValue, setSelectedValue] = useState(null);
  const [customRange, setCustomRange] = useState([]);
  const [asinList, setAsinList] = useState([]);
  const [selectedAsins, setSelectedAsins] = useState([]);

  const [periodicity, setPeriodicity] = useState('day');
  const [otherRange, setOtherRange] = useState({
    start_date: dayjs().subtract(29, 'day').format('YYYY-MM-DD'),
    end_date: dayjs().format('YYYY-MM-DD'),
  });

  const [filters, setFilters] = useState({
    page: 1,
    'per-page': 20,
    name: '',
    marketplace_id: null,
  });

  // Memoized values
  const dateFilterOptions = useMemo(() => {
    const options = Object.entries(filterData).map(([key, group]) => ({
      label: Object.values(group).join(' / '),
      value: key
    }));
    // options.push({ label: 'Custom Range', value: 'custom' });
    return options;
  }, [filterData]);

  // Data fetching
  const fetchInitialData = async () => {
    try {
      await Promise.all([
        GetMarketplace(),
        getFiltersData(),
        fetchProdctList()
      ]);
    } catch (error) {
      message.error('Failed to initialize dashboard data');
    }
  };

  const GetMarketplace = async () => {
    const marketplace = await GetMarketplaceDropdown();
    if (Array.isArray(marketplace?.marketplace_list) && marketplace.marketplace_list.length > 0) {
      const selectedMarketplaceEntry = Object.entries(marketplace.selected_marketplace || {})[0];
      const [marketplaceId] = selectedMarketplaceEntry || [];
      const firstMarketplace = marketplace.marketplace_list.find((m) => m.id === marketplaceId);

      setMarketplaceList(marketplace.marketplace_list);
      setSelectedMarketplace(firstMarketplace);
      setFilters(prev => ({
        ...prev,
        marketplace_id: marketplaceId,
      }));
    }
  };

  const getFiltersData = async () => {
    if (!userData?.token) {
      message.error("User not authenticated.");
      return;
    }

    try {
      const response = await MakeApiCall(
          `dashboard/tiles/get-filters-data`,
          "get",
          null,
          true,
          {},
          userData.token
      );

      if (response?.status) {
        setFilterData(response.data || {});
      }
    } catch (error) {
      message.warning("Failed to fetch filters.");
    }
  };

  const fetchProdctList = async () => {
    try {
      const response = await MakeApiCall(
          'get-products?no_pagination=true',
          'get',
          null,
          true,
          {},
          userData?.token
      );
      if (response?.status) {
        const asins = (response?.data?.records || []).map(item => ({
          asin: item.asin,
          name: item.item_name,
          image: item.item_image,
          sku: item.sku,
        }));
        setAsinList(asins);
      }
    } catch (error) {
      message.error('Error fetching products.');
    }
  };

  // Handlers
  const handleSelectChange = (value) => {
    setSelectedValue(value);
    if (value !== 'custom') {
      setCustomRange([]);
    }
  };

  // BUG FIX: Destructure dates safely, defaults to [null, null] if dates is null (on clear)
  const handleRangeChange = (dates) => {
    const [start, end] = dates || [null, null]; // Fix applied here

    if (start && end) {
      setCustomRange({
        raw: [start, end],
        formatted: {
          start_date: start.format('YYYY-MM-DD'),
          end_date: end.format('YYYY-MM-DD'),
        },
      });
      setSelectedValue('custom');
    } else {
      setCustomRange([]);
    }
  };

  // BUG FIX: Destructure dates safely, defaults to [null, null] if dates is null (on clear)
  const handleRangeOther = (dates) => {
    const [start, end] = dates || [null, null]; // Fix applied here

    if (start && end) {
      const dateRange = {
        raw: [start, end],
        formatted: {
          start_date: start.format('YYYY-MM-DD'),
          end_date: end.format('YYYY-MM-DD'),
        },
      };
      setOtherRange(dateRange.formatted);
    } else {
      // Optional: Reset to default range on clear
      setOtherRange({
        start_date: dayjs().subtract(29, 'day').format('YYYY-MM-DD'),
        end_date: dayjs().format('YYYY-MM-DD'),
      });
    }
  };

  // Effects
  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (filterData && Object.keys(filterData).length > 0 && !selectedValue) {
      const firstKey = Object.keys(filterData)[0];
      setSelectedValue(firstKey);
    }
  }, [filterData, selectedValue]);


  const handlePeriodicityChange = (value) => {
    setPeriodicity(value);
    // Optionally trigger a re-fetch or log event
  };

  const renderTab = (key, label, iconClass) => (
      <li className="nav-item" key={key}>
        <Link
            className={`nav-link ${activeTab === key ? 'active' : ''}`}
            onClick={() => setActiveTab(key)}
        >
          <i className={`ki-outline ${iconClass} me-3 fs-2 text-gray-900`}></i>
          {label}
        </Link>
      </li>
  );

  return (
      <Wrapper>
        <div className="mb-10 mx-n9 px-10 py-5 bg-white filter">
          <div className="position-relative d-flex wrap justify-content-between">
            <ul className="nav nav-tabs nav-line-tabs nav-stretch fs-6 d-inline-flex">
              {/*{renderTab('tiles', 'Tiles', 'ki-element-11')}*/}
              {renderTab('charts', 'Charts', 'ki-chart-pie-4')}
              {renderTab('pl', 'P&L', 'ki-chart-line-up')}
              {/*{renderTab('trends', 'Trends', 'ki-arrow-mix')}*/}
            </ul>
          </div>
        </div>

        <div className="mb-5 d-flex gap-4 flex-wrap filters">
          <Space wrap>
            <Select
                mode="multiple"
                placeholder="Select ASIN"
                style={{ minWidth: 250 }}
                size="large"
                value={selectedAsins}
                onChange={setSelectedAsins}
                loading={loading}
                optionLabelProp="value"
                maxTagCount={'responsive'}
            >
              {asinList.map(item => (
                  <Option key={item.asin} value={item.asin}>
                    <div className="d-flex align-items-center gap-2">
                      <img
                          src={item.image || DEFAULT_PRODUCT_IMAGE}
                          alt={item.name}
                          className="w-50px"
                      />
                      <div>
                        <div className="fw-500">{item.name}</div>
                        <div className="fs-13 text-gray-600">{item.asin}</div>
                      </div>
                    </div>
                  </Option>
              ))}
            </Select>

            { activeTab === 'tiles' ?
                <>
                  <Select
                      style={{ width: 300 }}
                      placeholder="Select Date Range"
                      onChange={handleSelectChange}
                      value={selectedValue}
                      size="large"
                  >
                    {dateFilterOptions.map(option => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                    ))}
                  </Select>

                  {selectedValue === 'custom' && (
                      <RangePicker
                          size="large"
                          onChange={handleRangeChange}
                          className="form-control d-flex fs-7 h-40px w-250px"
                          placeholder={['Start date', 'End date']}
                          suffixIcon={<i className="ki-duotone ki-calendar-8 fs-1 mb-n1 me-0"></i>}
                      />
                  )}
                </>
                :
                <>
                  <RangePicker
                      size="large"
                      presets={rangePresets}
                      value={[
                        dayjs(otherRange?.start_date),
                        dayjs(otherRange?.end_date),
                      ]}
                      onChange={handleRangeOther}
                      className="form-control d-flex fs-7 h-40px w-250px"
                      placeholder={['Start date', 'End date']}
                      suffixIcon={<i className="ki-duotone ki-calendar-8 fs-1 mb-n1 me-0"></i>}
                  />

                  <Select
                      value={periodicity}
                      style={{ width: 120 }}
                      onChange={handlePeriodicityChange}
                      size='large'
                  >
                    <Option value="day">By Day</Option>
                    <Option value="month">By Month</Option>
                  </Select>
                </>}

            <Select
                className="w-200px"
                size="large"
                placeholder="Select Marketplace"
                options={GetMarketplaceList(marketplaceList)}
                value={selectedMarketplace?.id}
                onChange={(e) => {
                  const selected = marketplaceList.find((m) => m.id === e);
                  setSelectedMarketplace(selected);
                  setFilters(prev => ({
                    ...prev,
                    marketplace_id: e,
                    page: 1,
                    'per-page': 20,
                  }));
                }}
            />
          </Space>
        </div>

        {/* Tab content */}
        {activeTab === 'tiles' && (
            <Tiles
                customRange={customRange}
                selectedValue={selectedValue}
                userData={userData}
                selectedMarketplace={selectedMarketplace}
                selectedCurrencySign={selectedCurrencySign}
            />
        )}
        {activeTab === 'charts' && (
            <ChartComponent
                periodicity={periodicity}
                otherRange={otherRange}
                userData={userData}
                selectedMarketplace={selectedMarketplace}
                selectedCurrencySign={selectedCurrencySign}
                selectedSku={selectedAsins.map(
                    asin => asinList.find(item => item.asin === asin)?.sku
                )}
            />
        )}
        {activeTab === 'pl' && (
            <PL
                periodicity={periodicity}
                otherRange={otherRange}
                userData={userData}
                selectedMarketplace={selectedMarketplace}
                selectedCurrencySign={selectedCurrencySign}
                selectedSku={selectedAsins.map(
                    asin => asinList.find(item => item.asin === asin)?.sku
                )}
            />
        )}
        {activeTab === 'trends' && <Trends />}
      </Wrapper>
  );
};


export default Dashboard;